import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaSettings, User } from "../AuthContext";
import ObservatorHandler from "./ObservatorHandler";
import { UserIcon, Video, Smartphone, RotateCw, Wifi } from "lucide-react";
import Scene from "./Scene";
import { RoomAccessRequest, SessionSettingsResponse, AccessRoomResponseOptions } from "./types";
import ActionButtons from "./ActionButtons";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { useRealtimeKitClient } from "@cloudflare/realtimekit-react";
import Swal from "sweetalert2";
import { httpsCallable } from "firebase/functions";
import { addUserToRoom, db, createVirtualAvatar, createSceneObject, getRoleInSession, functions, removeUserFromRoom } from "../../firebase";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { toMarketingUrl } from "../../constants/siteDomains";
import InviteUserModal from "./InviteUserModal";
import DyteMeet from "./DyteMeet";
import ParticipantsPanelModal from "./ParticipantsPanelModal";
import VirtualAvatarModal from "./VirtualAvatarModal";
import SceneObjectModal from "./SceneObjectModal";
import { useCreditBalance } from "../../hooks/useCreditBalance";
import MicDisabledBanner from "./MicDisabledBanner";
import MobileLowAudioBanner from "./MobileLowAudioBanner";
import RoomOnboardingTour from "./RoomOnboardingTour";
import { getRoomLayoutMode, type RoomLayoutMode } from "./mobileLayout";
import { playAccessRequestSound } from "./notificationSound";
import { logConnectionEvent, markBackgroundReturn, type ConnectionEventType } from "./connectionTelemetry";
import {
    evaluateNetworkQuality,
    joinVideoDefaults,
    networkWarningMessage,
    subscribeNetworkChanges,
    videoConstraintsForTier,
    videoConstraintsForParticipantCount,
    type NetworkTier,
} from "./networkQuality";
import { markRejoinIntent, clearRejoinIntent, saveAvatarPosition } from "./roomRejoin";
import { startBackgroundKeepAlive } from "./backgroundKeepAlive";
import {
    enableVideoAndRestoreBackground,
    installVirtualBackgroundCapture,
} from "./virtualBackgroundPersist";
import { boostRemoteMediaElements, ensureMobileSpeakerOutput } from "./mobileSpeakerRouting";
import {
    reconnectDelayForVisibility,
    reconnectStampedeExtraMs,
    ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS,
    ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS_DESKTOP,
    PASSIVE_RECOVERY_CHECK_MS_DESKTOP,
    PASSIVE_RECOVERY_CHECK_MS_MOBILE,
    SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS,
    SOCKET_DISCONNECTED_GRACE_MS_DESKTOP,
    SOCKET_DISCONNECTED_GRACE_MS_MOBILE,
    SOCKET_RECONNECTING_GRACE_MS_DESKTOP,
    SOCKET_RECONNECTING_GRACE_MS_MOBILE,
    shouldForceExitOnRoomLeft,
    isDocumentHidden,
    isMediaTransportHealthy,
    shouldPreserveSessionDespiteSignalingLoss,
    shouldWaitForSdkAfterVisibilityResume,
} from "./meetingReconnect";
import {
    collectWebRtcStats,
    WEBRTC_DIAG_EVENT_TYPES,
    type WebRtcSnapshot,
} from "./webrtcStats";

type RoomProps = {
    user: User | null;
    roomId: string;
    sessionSetting: SessionSettingsResponse | null;
    selectedAvatarId: string | null;
    sessionIdentityKey: string | null;
}

type MobileOS = "android" | "ios" | "other";

const detectMobileOS = (): MobileOS => {
    const nav = window.navigator as Navigator & {
        userAgentData?: { platform?: string };
    };

    const platform = (nav.userAgentData?.platform || nav.platform || "").toLowerCase();
    const userAgent = (nav.userAgent || "").toLowerCase();

    const isAndroid = /android/.test(userAgent) || platform.includes("android");
    const isIOS =
        /iphone|ipad|ipod/.test(userAgent) ||
        /iphone|ipad|ipod/.test(platform) ||
        (platform.includes("mac") && nav.maxTouchPoints > 1);

    if (isAndroid) return "android";
    if (isIOS) return "ios";
    return "other";
};

const FOREGROUND_TOAST_COOLDOWN_MS = 3 * 60 * 1000;
const VB_RESTORE_TOAST_COOLDOWN_MS = 2 * 60 * 1000;

function showStayInForegroundToast(lastShownAtRef: { current: number }): void {
    const now = Date.now();
    if (now - lastShownAtRef.current < FOREGROUND_TOAST_COOLDOWN_MS) {
        return;
    }
    lastShownAtRef.current = now;
    void Swal.fire({
        toast: true,
        position: "top",
        icon: "info",
        title: "Mantenha a aba da sala em primeiro plano durante a sessão",
        showConfirmButton: false,
        timer: 4500,
        timerProgressBar: true,
    });
}

function showVirtualBackgroundRestoreTip(lastShownAtRef: { current: number }): void {
    const now = Date.now();
    if (now - lastShownAtRef.current < VB_RESTORE_TOAST_COOLDOWN_MS) {
        return;
    }
    lastShownAtRef.current = now;
    void Swal.fire({
        toast: true,
        position: "top",
        icon: "info",
        title: "Se o fundo da câmera sumiu, reative em Configurações da câmera",
        showConfirmButton: false,
        timer: 5500,
        timerProgressBar: true,
    });
}

const Room: React.FC<RoomProps> = ({ user, roomId, sessionSetting, selectedAvatarId, sessionIdentityKey }) => {
    const navigate = useNavigate();

    const destroySessionCallable = httpsCallable(
        functions,
        "destroySession",
    );

    const cancelRoomAccessRequestFunction = httpsCallable<{ roomId: string }, { success: boolean; message: string }>(
        functions,
        "cancelRoomAccessRequest",
    );

    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.innerWidth < 1024,
    );
    const [activeTab, setActiveTab] = useState<"scene" | "video">(() =>
        typeof window !== "undefined" && window.innerWidth < 1024 ? "scene" : "video"
    );
    const [isPortrait, setIsPortrait] = useState(false);
    const [layoutMode, setLayoutMode] = useState<RoomLayoutMode>(() =>
        getRoomLayoutMode(window.innerWidth, window.innerHeight)
    );
    const [mobileOS, setMobileOS] = useState<MobileOS>("other");
    const [isRotationIconHorizontal, setIsRotationIconHorizontal] = useState(false);
    const [showOrientationModal, setShowOrientationModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isLeavingRoom, setIsLeavingRoom] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isVirtualAvatarModalOpen, setIsVirtualAvatarModalOpen] = useState(false);
    const [isSceneObjectModalOpen, setIsSceneObjectModalOpen] = useState(false);
    const [accessRequests, setAccessRequests] = useState<RoomAccessRequest[]>([]);
    const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
    const [isObserverInSession, setIsObserverInSession] = useState(false);
    const [forceRoomTour, setForceRoomTour] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<"connected" | "reconnecting">("connected");
    const [isPoorConnection, setIsPoorConnection] = useState(false);
    const [slowNetworkWarning, setSlowNetworkWarning] = useState<string | null>(null);
    const reconnectFallbackTimer = useRef<number | null>(null);
    const passiveRecoveryTimerRef = useRef<number | null>(null);
    const isReconnectingRef = useRef(false);
    const reconnectAttemptsRef = useRef(0);
    const recoveryHandlerRef = useRef<() => void>(() => {});
    const lowVideoAppliedRef = useRef(false);
    // Durante reconexão suave, leaveRoom() dispara roomLeft("left") — ignoramos
    // esse evento para não mandar o usuário ao painel no meio da recuperação.
    const softReconnectInProgressRef = useRef(false);
    /** Grace pós-leaveRoom: roomLeft assíncrono após reconexão suave. */
    const softReconnectGraceUntilRef = useRef(0);
    // Serializa performJoin: evita race quando soft reconnect e init inicial
    // disputam token/init ao mesmo tempo (caso Daniele: desktop com boa rede).
    const joinChainRef = useRef<Promise<unknown>>(Promise.resolve());
    const wasPageHiddenRef = useRef(false);
    const networkTierAppliedRef = useRef<NetworkTier | null>(null);
    const resumedFromBackgroundRef = useRef(false);
    /** Tentativa nativa do socket RTK (socketConnectionUpdate.reconnectionAttempt). */
    const sdkSocketReconnectAttemptRef = useRef(0);
    // Câmera desligada só por rede lenta (2G) — religamos se a rede melhorar.
    const videoOffForNetworkRef = useRef(false);
    const hadVideoBeforeNetworkOffRef = useRef(false);
    /** Vídeo reduzido por tamanho do grupo (4+ participantes), distinto de rede lenta. */
    const groupVideoAppliedRef = useRef(false);
    // Lembra se a câmera estava LIGADA quando o app foi para segundo plano, para
    // religá-la automaticamente ao voltar — e só nesse caso (não liga vídeo de
    // quem estava com a câmera desligada ou de observador sem vídeo).
    // Usado só no mobile (no desktop não pausamos mais o vídeo ao trocar de aba).
    const videoWasOnBeforeHideRef = useRef(false);
    const videoPauseDebounceRef = useRef<number | null>(null);
    const lastForegroundToastAtRef = useRef(0);
    const lastVbRestoreToastAtRef = useRef(0);
    // Timer que limpa o aviso de "conexão instável" depois que paramos de
    // receber sinais de conexão ruim. Sem isso, o aviso ficava preso para
    // sempre após o primeiro sinal (aparecia pra todo mundo no celular).
    const poorConnectionTimerRef = useRef<number | null>(null);
    const previousPendingRequestsCount = useRef(0);
    const hasInitializedRequests = useRef(false);
    const hasShownLowCreditAlert = useRef(false);
    // true somente quando o usuário clica explicitamente em "Sair". Usado para
    // impedir que o evento roomLeft do Dyte (queda de rede, app em segundo plano)
    // dispare o encerramento da sala para todos os participantes.
    const intentionalLeaveRef = useRef(false);
    // true quando o PRÓPRIO sistema dispara uma navegação/reload (sair, ser
    // expulso/encerrado, reload de recuperação). Suprime o aviso de
    // beforeunload nesses casos — o aviso é só para saídas manuais do usuário
    // (fechar a aba, voltar, digitar outra URL).
    const programmaticNavRef = useRef(false);
    // true assim que o doc dyteMeetings/{roomId} é visto existindo ao menos uma
    // vez. Evita tratar a ausência inicial (reunião ainda sendo criada pelo
    // getDyteMeetingV2) como "sessão encerrada" e expulsar quem acabou de entrar.
    const meetingDocExistedRef = useRef(false);
    /** Estado ao vivo do doc dyteMeetings/{roomId} — distingue rotação RTK de encerramento real. */
    const meetingDocExistsRef = useRef(false);
    // Telemetria de queda: helper com contexto sempre atualizado (sem poluir
    // dependências dos callbacks de reconexão) e flag para registrar a volta.
    const logConnRef = useRef<
        (
            type: ConnectionEventType,
            extra?: {
                roomLeftState?: string | null;
                meetingExists?: boolean | null;
                socketReconnectionAttempt?: number | null;
                mediaStillConnected?: boolean | null;
            },
        ) => void
    >(() => {});
    /** Último getStats válido — usado se o PC já fechou no momento da queda. */
    const lastWebRtcSnapshotRef = useRef<WebRtcSnapshot | null>(null);
    const wasReconnectingRef = useRef(false);

    // Co-admins: lista (ao vivo) de moderadores promovidos, lida do doc da sala.
    // Permite que um participante recém-promovido ganhe os poderes na hora, sem
    // precisar reentrar.
    const [liveModerators, setLiveModerators] = useState<string[]>([]);

    // DONO da sala (terapeuta criador): ÚNICO que encerra a sessão para todos.
    // Fallback para isRoomModerator mantém compatibilidade enquanto o backend novo
    // (que envia isRoomOwner) não está publicado.
    const isOwner = sessionSetting?.isRoomOwner ?? sessionSetting?.isRoomModerator ?? false;
    // MODERADOR (ao vivo) = dono OU co-admin promovido. Gate de todas as ações de
    // moderação na UI (aprovar entrada, promover/rebaixar, expulsar, mutar, cenário).
    const isModerator =
        isOwner ||
        (sessionSetting?.isRoomModerator ?? false) ||
        (!!user?.uid && liveModerators.includes(user.uid));

    const { hoursBalance, isLowBalance } = useCreditBalance(
        user?.uid,
        isOwner
    );

    const [meetingClient, setMeetingClient] = useState<RealtimeKitClient | null>(null);
    /** Mantém cena/avatares montados durante reconexão (não depender só de meetingClient). */
    const [sessionJoined, setSessionJoined] = useState(false);
    const [, initMeeting] = useRealtimeKitClient();
    const initMeetingRef = useRef(initMeeting);
    const isInitializingRef = useRef(false);
    const { mediaSettings: nestedMediaSettings } = useMediaSettings();
    const stableMediaSettings = useMemo(
        () => nestedMediaSettings,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(nestedMediaSettings)],
    );

    // Escuta a lista de co-admins (moderators[]) ao vivo no doc da sala.
    useEffect(() => {
        if (!roomId) return;
        const roomRef = doc(db, "rooms", roomId);
        const unsub = onSnapshot(roomRef, (snap) => {
            const mods = snap.data()?.moderators;
            setLiveModerators(Array.isArray(mods) ? mods : []);
        });
        return () => unsub();
    }, [roomId]);

    // Quando um participante é promovido a co-admin DURANTE a sessão, renovamos o
    // token de vídeo (soft reconnect) para que o novo token traga o preset de host
    // — só assim ele ganha o poder de expulsar/mutar pelo vídeo. O dono já entra
    // com host, então não precisa (guard !isOwner). Quem já entrou como co-admin
    // (sessionSetting) também não dispara, pois isModerator já começa true.
    const wasModeratorRef = useRef(isModerator);
    useEffect(() => {
        const becameModerator = isModerator && !wasModeratorRef.current;
        wasModeratorRef.current = isModerator;
        if (becameModerator && !isOwner && meetingClient) {
            recoveryHandlerRef.current?.();
        }
    }, [isModerator, isOwner, meetingClient]);

    const handleSceneError = useCallback((error: string) => {
        Swal.fire({
            icon: 'error',
            title: 'Erro na sala',
            text: error,
        });
    }, []);

    const checkOrientation = useCallback(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const mobile = width < 1024;
        setIsPortrait(mobile && height > width);
        setIsMobile(mobile);
        setLayoutMode(getRoomLayoutMode(width, height));
    }, []);

    useEffect(() => {
        setMobileOS(detectMobileOS());
    }, []);

    useEffect(() => {
        if (!isMobile || !isPortrait) {
            setIsRotationIconHorizontal(false);
            setShowOrientationModal(false);
            return;
        }

        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (prefersReducedMotion) {
            setIsRotationIconHorizontal(true);
            return;
        }

        const intervalId = window.setInterval(() => {
            setIsRotationIconHorizontal((prev) => !prev);
        }, 1200);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [isMobile, isPortrait]);

    useEffect(() => {
        const handleOrientationChange = () => {
            setTimeout(checkOrientation, 100);
        };

        checkOrientation();
        window.addEventListener("resize", checkOrientation);
        window.addEventListener("orientationchange", handleOrientationChange);
        return () => {
            window.removeEventListener("resize", checkOrientation);
            window.removeEventListener("orientationchange", handleOrientationChange);
        };
    }, [checkOrientation]);

    // Fluxo único de entrada na sala (token novo + init + join). Reutilizado tanto
    // na primeira entrada quanto na reconexão suave, garantindo que o token Dyte
    // seja sempre renovado (resolve expiração de token em sessões longas).
    const performJoin = useCallback(async (options?: { isReconnect?: boolean }): Promise<RealtimeKitClient | undefined> => {
        const runJoin = async (): Promise<RealtimeKitClient | undefined> => {
            const getDyteMeeting = httpsCallable<
                { room: string; roleInSession?: string; isReconnect?: boolean },
                { authToken: string; meetingId: string }
            >(functions, "getDyteMeetingV2");

            const result = await getDyteMeeting({
                room: roomId,
                roleInSession: getRoleInSession(sessionSetting?.sessionRole || "participant"),
                isReconnect: options?.isReconnect ?? false,
            });

            const authToken = result.data.authToken;
            const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
            const videoDefaults = joinVideoDefaults(isMobileDevice);

            const meeting = await initMeetingRef.current({
                authToken,
                defaults: {
                    audio: stableMediaSettings.audio,
                    video: stableMediaSettings.video,
                    mediaConfiguration: {
                        video: {
                            width: { ideal: videoDefaults.width },
                            height: { ideal: videoDefaults.height },
                            frameRate: { ideal: videoDefaults.frameRate },
                        },
                    },
                },
                overrides: {
                    simulcastConfig: {
                        disable: false,
                        encodings: [
                            {
                                rid: "q",
                                scaleResolutionDownBy: 2,
                                maxBitrate: videoDefaults.maxBitrateLow,
                            },
                            {
                                rid: "f",
                                scaleResolutionDownBy: 1,
                                maxBitrate: videoDefaults.maxBitrateHigh,
                            },
                        ],
                    },
                },
            });

            if (!meeting) {
                return undefined;
            }

            await meeting.joinRoom();

            await addUserToRoom(
                roomId,
                user as User,
                sessionSetting?.sessionRole || "participant",
                selectedAvatarId,
                sessionIdentityKey,
            );

            return meeting;
        };

        const chained = joinChainRef.current.then(runJoin, runJoin);
        joinChainRef.current = chained.then(
            () => undefined,
            () => undefined,
        );
        return chained;
    }, [roomId, selectedAvatarId, sessionIdentityKey, sessionSetting?.sessionRole, stableMediaSettings, user, mobileOS]);

    const initializeAndJoinDyte = useCallback(async () => {
        if (isInitializingRef.current || meetingClient) {
            return;
        }

        isInitializingRef.current = true;

        try {
            setLoadingMessage("Conectando à sala...");
            const meeting = await performJoin();

            if (!meeting) {
                console.error("❌ Meeting client não foi criado");
                return;
            }

            setMeetingClient(meeting);
            setSessionJoined(true);
        } catch (error) {
            console.error(error);
            setLoadingMessage("Erro ao entrar na sala.");

            Swal.fire({
                icon: "error",
                title: "Erro ao entrar na sala",
                text: "Ocorreu um erro ao tentar entrar na sala. Por favor, tente novamente mais tarde.",
            });
        } finally {
            isInitializingRef.current = false;
        }
    }, [meetingClient, performJoin]);

    useEffect(() => {
        initializeAndJoinDyte();
    }, [initializeAndJoinDyte]);

    useEffect(() => {
        if (!user?.uid || !roomId) return;

        const participantRef = doc(db, "rooms", roomId, "participants", user.uid);
        const unsubscribe = onSnapshot(participantRef, (snap) => {
            if (!snap.exists()) {
                setIsObserverInSession(false);
                return;
            }
            const data = snap.data();
            setIsObserverInSession(data?.roleInSession === "observer");

            // Guarda a última posição conhecida do PRÓPRIO avatar. Se o doc for
            // recriado do zero numa reentrada (ex.: limpeza do monitor), usamos
            // isso para restaurar a posição em vez de randomizar — o usuário
            // "volta como estava".
            if (typeof data?.xRel === "number" && typeof data?.yRel === "number") {
                saveAvatarPosition(roomId, user.uid, {
                    xRel: data.xRel,
                    yRel: data.yRel,
                    pose: data?.pose ?? null,
                    direction: data?.direction ?? null,
                    avatarId: data?.avatarId ?? null,
                });
            }
        });

        return () => unsubscribe();
    }, [roomId, user?.uid]);

    // Heartbeat de "intenção de reentrada": enquanto o usuário está ativo na
    // sala, marcamos isso no localStorage (com TTL curto). Se a sessão cair —
    // inclusive quando o SO do celular mata a aba em segundo plano e a página
    // recarrega do zero — o Lobby detecta essa intenção e REENTRA sozinho na
    // mesma sala, sem o usuário precisar "pedir para entrar" de novo.
    useEffect(() => {
        if (!user?.uid || !roomId) return;

        const ping = () => {
            if (intentionalLeaveRef.current) return;
            markRejoinIntent(roomId, user.uid, selectedAvatarId, sessionIdentityKey);
        };

        ping();
        const intervalId = window.setInterval(ping, 30000);
        return () => window.clearInterval(intervalId);
    }, [roomId, user?.uid, selectedAvatarId, sessionIdentityKey]);

    const disableStreams = useCallback(() => {
        if (meetingClient?.self) {
            // Cada chamada é isolada: o SDK pode rejeitar chamadas concorrentes
            // (ex.: disableAudio já em andamento) e não queremos rejeições soltas.
            void Promise.resolve(meetingClient.self.disableVideo()).catch(() => {});
            void Promise.resolve(meetingClient.self.disableAudio()).catch(() => {});
            void Promise.resolve(meetingClient.self.disableScreenShare()).catch(() => {});
        }
    }, [meetingClient]);

    // Mantém uma referência ao cliente para liberar o microfone/câmera no
    // unmount sem prender o efeito de cleanup a cada mudança de meetingClient.
    const meetingClientCleanupRef = useRef<RealtimeKitClient | null>(null);
    useEffect(() => {
        meetingClientCleanupRef.current = meetingClient;
    }, [meetingClient]);

    // Rede de segurança: ao desmontar a sala (ex.: navegação SPA pelo botão
    // "voltar" do navegador, que não dispara reload), garante que o cliente saia
    // da reunião e LIBERE câmera/microfone. Sem isso o mic pode ficar preso e o
    // Android entende que há "ligação em andamento", bloqueando a gravação de
    // áudio em outros apps (ex.: WhatsApp).
    useEffect(() => {
        return () => {
            if (passiveRecoveryTimerRef.current !== null) {
                window.clearTimeout(passiveRecoveryTimerRef.current);
                passiveRecoveryTimerRef.current = null;
            }
            const client = meetingClientCleanupRef.current;
            if (client?.self) {
                void Promise.resolve(client.self.disableAudio()).catch(() => {});
                void Promise.resolve(client.self.disableVideo()).catch(() => {});
            }
            void Promise.resolve(client?.leaveRoom?.()).catch(() => {});
        };
    }, []);

    // Mantém o helper de telemetria com o contexto mais recente (roomId, user,
    // device, estado do socket) sem forçar os callbacks de reconexão a depender
    // dessas variáveis. Em quedas, anexa snapshot WebRTC (candidate pair, loss…).
    useEffect(() => {
        logConnRef.current = (type, extra) => {
            void (async () => {
                let webrtc: WebRtcSnapshot | null = lastWebRtcSnapshotRef.current;
                if (WEBRTC_DIAG_EVENT_TYPES.has(type) && meetingClient) {
                    const fresh = await collectWebRtcStats(meetingClient);
                    if (fresh && !fresh.error) {
                        lastWebRtcSnapshotRef.current = fresh;
                        webrtc = fresh;
                    } else if (fresh && lastWebRtcSnapshotRef.current) {
                        // Mantém stats de mídia do último snapshot válido; atualiza
                        // ICE/streams (ainda legíveis mesmo com PC fechado).
                        webrtc = {
                            ...lastWebRtcSnapshotRef.current,
                            error: fresh.error,
                            collectedAt: fresh.collectedAt,
                            iceConnectionState:
                                fresh.iceConnectionState ??
                                lastWebRtcSnapshotRef.current.iceConnectionState,
                            connectionState:
                                fresh.connectionState ??
                                lastWebRtcSnapshotRef.current.connectionState,
                            iceGatheringState:
                                fresh.iceGatheringState ??
                                lastWebRtcSnapshotRef.current.iceGatheringState,
                            participantCount:
                                fresh.participantCount ??
                                lastWebRtcSnapshotRef.current.participantCount,
                            streamCount:
                                fresh.streamCount ??
                                lastWebRtcSnapshotRef.current.streamCount,
                            localAudioEnabled:
                                fresh.localAudioEnabled ??
                                lastWebRtcSnapshotRef.current.localAudioEnabled,
                            localVideoEnabled:
                                fresh.localVideoEnabled ??
                                lastWebRtcSnapshotRef.current.localVideoEnabled,
                        };
                    } else {
                        webrtc = fresh;
                    }
                }

                logConnectionEvent(type, {
                    roomId,
                    userId: user?.uid ?? null,
                    role: sessionSetting?.sessionRole ?? null,
                    device: mobileOS,
                    socketState: meetingClient?.meta?.socketState?.state ?? null,
                    reconnectAttempt: reconnectAttemptsRef.current,
                    roomLeftState: extra?.roomLeftState ?? null,
                    meetingExists: extra?.meetingExists ?? meetingDocExistsRef.current,
                    mediaStillConnected: extra?.mediaStillConnected ?? null,
                    socketReconnectionAttempt: extra?.socketReconnectionAttempt ?? null,
                    webrtc,
                });
            })();
        };
    }, [roomId, user?.uid, sessionSetting?.sessionRole, mobileOS, meetingClient]);

    // Amostra getStats a cada 10s enquanto conectado — na queda o PC pode já
    // estar fechado; o último snapshot preserva candidate pair / loss / RTT.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        let cancelled = false;
        const sample = async () => {
            const snap = await collectWebRtcStats(meetingClient);
            if (!cancelled && snap && !snap.error) {
                lastWebRtcSnapshotRef.current = snap;
            }
        };

        void sample();
        const intervalId = window.setInterval(() => {
            void sample();
        }, 10_000);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [meetingClient]);

    const cancelConnectionRecovery = useCallback(() => {
        if (reconnectFallbackTimer.current !== null) {
            window.clearTimeout(reconnectFallbackTimer.current);
            reconnectFallbackTimer.current = null;
        }
    }, []);

    const cancelPassiveRecoveryCheck = useCallback(() => {
        if (passiveRecoveryTimerRef.current !== null) {
            window.clearTimeout(passiveRecoveryTimerRef.current);
            passiveRecoveryTimerRef.current = null;
        }
    }, []);

    const schedulePassiveRecoveryCheck = useCallback(() => {
        cancelPassiveRecoveryCheck();
        if (intentionalLeaveRef.current) {
            return;
        }
        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
        const delayMs = isMobileDevice
            ? PASSIVE_RECOVERY_CHECK_MS_MOBILE
            : PASSIVE_RECOVERY_CHECK_MS_DESKTOP;
        passiveRecoveryTimerRef.current = window.setTimeout(() => {
            passiveRecoveryTimerRef.current = null;
            if (intentionalLeaveRef.current || !meetingDocExistsRef.current) {
                return;
            }
            const mediaOk = isMediaTransportHealthy(lastWebRtcSnapshotRef.current);
            if (!mediaOk) {
                recoveryHandlerRef.current?.();
                return;
            }
            schedulePassiveRecoveryCheck();
        }, delayMs);
    }, [cancelPassiveRecoveryCheck, mobileOS]);

    /** Evita soft reconnect quando só o signaling oscilou e a mídia segue ok. */
    const tryPreserveSessionDespiteSignalingLoss = useCallback((): boolean => {
        const mediaOk = isMediaTransportHealthy(lastWebRtcSnapshotRef.current);
        if (!shouldPreserveSessionDespiteSignalingLoss(mediaOk, meetingDocExistsRef.current)) {
            return false;
        }
        cancelConnectionRecovery();
        if (wasReconnectingRef.current) {
            wasReconnectingRef.current = false;
            logConnRef.current("reconnected");
        }
        reconnectAttemptsRef.current = 0;
        setConnectionStatus("connected");
        schedulePassiveRecoveryCheck();
        return true;
    }, [cancelConnectionRecovery, schedulePassiveRecoveryCheck]);

    // Agenda a recuperação: damos uma janela para o Dyte reconectar sozinho
    // (caminho mais comum). Se não voltar, disparamos a reconexão suave
    // (re-init com token novo) e, em último caso, reload — sempre na MESMA sala
    // (getDyteMeetingV2 reaproveita o meeting), nunca caindo pro painel.
    // Delays maiores quando o SDK está em "reconnecting" — deixa a reconexão
    // nativa terminar antes de interferir com leaveRoom + token novo.
    const defaultRecoveryDelayMs = mobileOS === "android" || mobileOS === "ios" ? 12000 : 40000;

    const scheduleConnectionRecovery = useCallback((delayMs = defaultRecoveryDelayMs) => {
        if (reconnectFallbackTimer.current !== null) {
            window.clearTimeout(reconnectFallbackTimer.current);
            reconnectFallbackTimer.current = null;
        }
        const effectiveDelay = reconnectDelayForVisibility(delayMs);
        // Queda geral: espalha soft reconnect (moderador primeiro, participantes depois).
        const stampedeExtra = reconnectStampedeExtraMs({
            userId: user?.uid,
            isModerator,
        });
        const totalDelay = effectiveDelay + stampedeExtra;
        reconnectFallbackTimer.current = window.setTimeout(() => {
            reconnectFallbackTimer.current = null;
            if (!intentionalLeaveRef.current) {
                recoveryHandlerRef.current?.();
            }
        }, totalDelay);
    }, [defaultRecoveryDelayMs, user?.uid, isModerator]);

    const markReconnecting = useCallback((recoveryDelayMs?: number) => {
        if (intentionalLeaveRef.current) {
            return;
        }
        if (!wasReconnectingRef.current) {
            wasReconnectingRef.current = true;
            logConnRef.current("reconnecting");
        }
        setConnectionStatus("reconnecting");
        scheduleConnectionRecovery(recoveryDelayMs);
    }, [scheduleConnectionRecovery]);

    const markConnected = useCallback(() => {
        // Registra a recuperação só se estávamos reconectando.
        if (wasReconnectingRef.current) {
            wasReconnectingRef.current = false;
            logConnRef.current("reconnected");
        }
        reconnectAttemptsRef.current = 0;
        setConnectionStatus("connected");
        cancelConnectionRecovery();
        cancelPassiveRecoveryCheck();
    }, [cancelConnectionRecovery, cancelPassiveRecoveryCheck]);

    // Reconexão suave: tenta restabelecer a sessão sem recarregar a página,
    // buscando um token novo e reinicializando o cliente Dyte. Só recorre ao
    // reload se a reconexão suave falhar repetidamente (1× no celular, 2× no desktop).
    const attemptSoftReconnect = useCallback(async () => {
        if (intentionalLeaveRef.current || isReconnectingRef.current) {
            return;
        }

        // Amostra fresca: o poll de 10s pode estar stale e liberar leave+rejoin
        // indevido com mídia ainda saudável.
        try {
            const fresh = await collectWebRtcStats(meetingClient);
            if (fresh && !fresh.error) {
                lastWebRtcSnapshotRef.current = fresh;
            }
        } catch {
            /* keep last snapshot */
        }

        if (tryPreserveSessionDespiteSignalingLoss()) {
            console.warn("Soft reconnect adiado: mídia WebRTC ainda ok (oscilação de signaling).");
            return;
        }

        // Não force leaveRoom/rejoin com a aba escondida — isso encerrava a
        // sessão em segundos ao ir para segundo plano (regressão observada).
        if (isDocumentHidden()) {
            setConnectionStatus("reconnecting");
            scheduleConnectionRecovery(defaultRecoveryDelayMs);
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
        const reloadAfterAttempts = isMobileDevice ? 2 : 2;
        const retryDelayMs = isMobileDevice ? 6000 : 6000;

        const oldClient = meetingClient;
        const socketState = oldClient?.meta?.socketState?.state;
        const nativeAttempt = sdkSocketReconnectAttemptRef.current;
        if (socketState === "connected") {
            markConnected();
            return;
        }
        if (
            socketState === "reconnecting" &&
            nativeAttempt > 0 &&
            nativeAttempt < SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS
        ) {
            setConnectionStatus("reconnecting");
            scheduleConnectionRecovery(
                isMobileDevice
                    ? SOCKET_RECONNECTING_GRACE_MS_MOBILE
                    : SOCKET_RECONNECTING_GRACE_MS_DESKTOP,
            );
            return;
        }

        isReconnectingRef.current = true;
        softReconnectInProgressRef.current = true;
        softReconnectGraceUntilRef.current = Date.now() + 12_000;
        setConnectionStatus("reconnecting");
        logConnRef.current("softReconnectStart");

        try {
            if (oldClient) {
                try {
                    await Promise.race([
                        Promise.resolve(oldClient.leaveRoom()),
                        new Promise<void>((_, reject) => {
                            window.setTimeout(() => reject(new Error("leaveRoom timeout")), 5000);
                        }),
                    ]);
                } catch (leaveErr) {
                    console.warn("Falha ao encerrar cliente antigo antes de reconectar:", leaveErr);
                }
                await new Promise((r) => window.setTimeout(r, 350));
            }

            const meeting = await performJoin({ isReconnect: true });
            if (!meeting) {
                throw new Error("Reconexão não retornou cliente");
            }

            lowVideoAppliedRef.current = false;
            setIsPoorConnection(false);
            setMeetingClient(meeting);
            setSessionJoined(true);
            logConnRef.current("softReconnectSuccess");
            markConnected();
            resumedFromBackgroundRef.current = false;
        } catch (error) {
            console.error("Falha na reconexão suave:", error);
            reconnectAttemptsRef.current += 1;
            logConnRef.current("softReconnectFail");

            const shouldReload =
                reconnectAttemptsRef.current >= reloadAfterAttempts;

            if (shouldReload && !intentionalLeaveRef.current) {
                logConnRef.current("reloadFallback");
                programmaticNavRef.current = true;
                window.location.reload();
            } else {
                reconnectFallbackTimer.current = window.setTimeout(() => {
                    reconnectFallbackTimer.current = null;
                    if (!intentionalLeaveRef.current) {
                        recoveryHandlerRef.current?.();
                    }
                }, retryDelayMs);
            }
        } finally {
            softReconnectGraceUntilRef.current = Date.now() + 8000;
            softReconnectInProgressRef.current = false;
            isReconnectingRef.current = false;
        }
    }, [meetingClient, performJoin, markConnected, mobileOS, scheduleConnectionRecovery, defaultRecoveryDelayMs, tryPreserveSessionDespiteSignalingLoss]);

    useEffect(() => {
        recoveryHandlerRef.current = attemptSoftReconnect;
    }, [attemptSoftReconnect]);

    // Effect separado para cleanup do meetingClient
    useEffect(() => {
        const dyteMeetingsRef = doc(db, "dyteMeetings", roomId);
        const unsubscribeDyteMeetingListener = onSnapshot(
            dyteMeetingsRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    // A reunião existe: marcamos para distinguir, mais tarde, um
                    // encerramento real (doc removido) da ausência inicial.
                    meetingDocExistedRef.current = true;
                    meetingDocExistsRef.current = true;
                    return;
                }

                meetingDocExistsRef.current = false;

                // Doc ausente ANTES de ter existido = estado inicial: o
                // getDyteMeetingV2 ainda está criando a reunião. NÃO encerrar aqui
                // (era a causa de "Sessão encerrada" imediato ao entrar).
                if (!meetingDocExistedRef.current) {
                    return;
                }

                // Saída intencional já é tratada pelo botão "Sair".
                if (intentionalLeaveRef.current) {
                    return;
                }

                // Doc existia e foi removido => sessão encerrada de fato. Mostra
                // o aviso para todos que NÃO são o dono (inclui co-admins).
                if (!isOwner) {
                    clearRejoinIntent(roomId);
                    Swal.fire({
                        icon: "info",
                        title: "Sessão encerrada",
                        text: "Esta sessão foi encerrada pelo terapeuta. Você será redirecionado.",
                    }).then(() => {
                        navigate("/");
                    });
                }
            },
            (error) => {
                console.error("❌ ERRO ao monitorar documento dyteMeetings:", error);
            },
        );

        return () => {
            unsubscribeDyteMeetingListener();
        };
    }, [navigate, roomId, isOwner]);

    const handleModeratorLeave = useCallback(async () => {
        await destroySessionCallable(roomId);
    }, [destroySessionCallable, roomId]);

    const handleCreateVirtualAvatar = useCallback(
        async ({ name, avatarId }: { name: string; avatarId: string }) => {
            if (!roomId || !user) {
                return;
            }

            if (!isModerator) {
                Swal.fire({
                    icon: "error",
                    title: "Ação não permitida",
                    text: "Somente o moderador pode adicionar avatares ao cenário.",
                });
                return;
            }

            try {
                await createVirtualAvatar(roomId, {
                    name,
                    avatarId,
                    createdBy: user.uid,
                    createdByName: user.name || user.email || null,
                });

                setIsVirtualAvatarModalOpen(false);
            } catch (error) {
                console.error("Erro ao criar avatar virtual:", error);
                Swal.fire({
                    icon: "error",
                    title: "Erro ao adicionar avatar",
                    text: "Não foi possível criar o avatar virtual. Tente novamente.",
                });
            }
        },
        [roomId, user, isModerator]
    );

    const handleCreateSceneObject = useCallback(
        async ({ name, objectId }: { name: string; objectId: string }) => {
            if (!roomId || !user) return;

            if (!isModerator) {
                Swal.fire({
                    icon: "error",
                    title: "Ação não permitida",
                    text: "Somente o moderador pode adicionar objetos ao cenário.",
                });
                return;
            }

            try {
                await createSceneObject(roomId, {
                    name,
                    objectId,
                    createdBy: user.uid,
                    createdByName: user.name || user.email || null,
                });
                setIsSceneObjectModalOpen(false);
            } catch (error) {
                console.error("Erro ao criar objeto:", error);
                Swal.fire({
                    icon: "error",
                    title: "Erro ao adicionar objeto",
                    text: "Não foi possível criar o objeto. Tente novamente.",
                });
            }
        },
        [roomId, user, isModerator]
    );

    const handleLeaveMeeting = useCallback(async () => {
        if (isLeavingRoom) return;

        // Apenas o DONO encerra a sessão para todos. Co-admins e participantes
        // saem normalmente (a sala continua aberta para os demais).
        // Fluxo do DONO: ao sair, a sessão é encerrada para TODOS. Pedimos
        // confirmação explícita (evita toque acidental) e só saímos de fato se o
        // backend confirmar o encerramento. Se falhar, o dono PERMANECE na
        // sala e é avisado — assim a sala nunca fica "órfã"/aberta sem aviso.
        if (isOwner) {
            const confirmResult = await Swal.fire({
                icon: "warning",
                title: "Encerrar a sessão para todos?",
                html:
                    "Você é o <strong>moderador</strong> desta sala. Ao encerrar, " +
                    "todos os participantes serão desconectados.<br/><br/>" +
                    "Se preferir continuar, a sala permanece aberta.",
                showCancelButton: true,
                confirmButtonText: "Sim, encerrar sessão",
                cancelButtonText: "Não, continuar na sala",
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
            });

            if (!confirmResult.isConfirmed) {
                return;
            }

            setIsLeavingRoom(true);
            try {
                await handleModeratorLeave();
            } catch (error) {
                console.error("❌ Erro ao encerrar a sessão:", error);
                setIsLeavingRoom(false);
                await Swal.fire({
                    icon: "error",
                    title: "Não foi possível encerrar",
                    text:
                        "A sala continua aberta. Verifique sua conexão e toque " +
                        "em Sair novamente para encerrar a sessão.",
                });
                return; // permanece na sala; a conexão segue intacta
            }

            // Encerrou com sucesso: agora sim podemos sair. Marca como saída
            // intencional para que o roomLeft do SDK não dispare reconexão.
            intentionalLeaveRef.current = true;
            clearRejoinIntent(roomId);
            disableStreams();
            meetingClient?.leaveRoom();
            setTimeout(() => {
                window.location.href = user?.isAnonymous ? toMarketingUrl("/") : "/painel";
            }, 500);
            return;
        }

        // Fluxo do PARTICIPANTE: apenas sai; a sala continua aberta para os demais.
        intentionalLeaveRef.current = true;
        clearRejoinIntent(roomId);
        setIsLeavingRoom(true);

        try {
            if (user && roomId) {
                await Promise.all([
                    cancelRoomAccessRequestFunction({ roomId }),
                    removeUserFromRoom(roomId, user.uid),
                ]);
            }
        } catch (error) {
            console.error("❌ Erro ao sair da reunião:", error);
        } finally {
            disableStreams();
            meetingClient?.leaveRoom();
            setTimeout(() => {
                window.location.href = user?.isAnonymous ? toMarketingUrl("/") : "/painel";
            }, 500);
        }
    }, [
        cancelRoomAccessRequestFunction,
        disableStreams,
        handleModeratorLeave,
        isLeavingRoom,
        isOwner,
        meetingClient,
        roomId,
        user,
    ]);

    // Refs para o handler do botão "voltar" (popstate) sem recriar o listener a
    // cada render.
    const handleLeaveMeetingRef = useRef(handleLeaveMeeting);
    const isOwnerRef = useRef(isOwner);
    useEffect(() => {
        handleLeaveMeetingRef.current = handleLeaveMeeting;
    }, [handleLeaveMeeting]);
    useEffect(() => {
        isOwnerRef.current = isOwner;
    }, [isOwner]);

    // Botão "voltar" do celular / gesto de voltar: é navegação interna (SPA) e
    // NÃO dispara beforeunload. Para o sistema não confundir com uma queda,
    // interceptamos o popstate, cancelamos a saída (recolocando um estado
    // sentinela) e pedimos confirmação. Só sai de fato se o usuário confirmar —
    // aí o fluxo de "Sair" cuida de liberar o microfone e, no caso do moderador,
    // encerrar a sessão.
    useEffect(() => {
        window.history.pushState(null, "", window.location.href);

        const onPopState = () => {
            if (intentionalLeaveRef.current || programmaticNavRef.current) {
                return; // saída já intencional/tratada: permite navegar
            }
            // Recoloca o sentinela para "desfazer" o voltar e manter na sala.
            window.history.pushState(null, "", window.location.href);

            if (isOwnerRef.current) {
                // handleLeaveMeeting já mostra a confirmação de encerrar a sessão.
                void handleLeaveMeetingRef.current?.();
                return;
            }

            void Swal.fire({
                icon: "warning",
                title: "Sair da sala?",
                text: "Você quer mesmo sair da sala? Se foi sem querer, continue na sessão.",
                showCancelButton: true,
                confirmButtonText: "Sim, sair",
                cancelButtonText: "Não, continuar na sala",
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
            }).then((result) => {
                if (result.isConfirmed) {
                    void handleLeaveMeetingRef.current?.();
                }
            });
        };

        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);

    // Handler do evento roomLeft do SDK do Dyte. NUNCA encerra a sala aqui:
    // encerrar a sessão é exclusivo do botão "Sair" do app (handleLeaveMeeting).
    // Isso impede que uma oscilação de rede do terapeuta (ou qualquer estado de
    // saída que o Dyte não classifique como "disconnected") destrua a sala e
    // derrube todos os participantes — causa raiz das quedas relatadas.
    const handleDyteRoomLeft = useCallback((state?: string) => {
        if (intentionalLeaveRef.current) {
            return;
        }

        const inSoftReconnectGrace =
            softReconnectInProgressRef.current ||
            isReconnectingRef.current ||
            Date.now() < softReconnectGraceUntilRef.current;

        // leaveRoom() durante reconexão suave emite roomLeft("left") — não é saída real.
        if (inSoftReconnectGrace) {
            console.warn("Dyte roomLeft ignorado (reconexão em andamento):", state);
            return;
        }

        const meetingExists = meetingDocExistsRef.current;
        const snap = lastWebRtcSnapshotRef.current;
        const mediaStillConnected = isMediaTransportHealthy(snap);

        // Encerramento pelo moderador, expulsão ou ended quando a reunião já
        // não existe no Firestore. roomLeft=ended COM doc ativo = rotação RTK → reconectar.
        if (shouldForceExitOnRoomLeft(state, meetingExists, mediaStillConnected)) {
            console.warn("Dyte roomLeft (saída legítima):", state, { meetingExists });
            logConnRef.current("roomLeft", { roomLeftState: state, meetingExists });
            programmaticNavRef.current = true;
            clearRejoinIntent(roomId);
            disableStreams();
            meetingClient?.leaveRoom();
            setTimeout(() => {
                window.location.href = user?.isAnonymous ? toMarketingUrl("/") : "/painel";
            }, 500);
            return;
        }

        // Signaling oscilou mas áudio/vídeo seguem — não derrubar a sessão.
        if (shouldPreserveSessionDespiteSignalingLoss(mediaStillConnected, meetingExists)) {
            console.warn("Dyte roomLeft ignorado (mídia ok, reunião ativa):", state);
            logConnRef.current("roomLeft", {
                roomLeftState: state ?? null,
                meetingExists,
                mediaStillConnected,
            });
            tryPreserveSessionDespiteSignalingLoss();
            return;
        }

        // "left"/"ended" (reunião ainda ativa)/disconnected = queda — recupera na mesma página.
        console.warn("Dyte roomLeft (falha de conexão, tentando reconectar):", state, {
            meetingExists,
            mediaStillConnected,
        });
        logConnRef.current("roomLeft", {
            roomLeftState: state ?? null,
            meetingExists,
            mediaStillConnected,
        });
        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
        const recoveryDelay =
            mediaStillConnected && !isMobileDevice
                ? ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS_DESKTOP
                : mediaStillConnected
                  ? ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS
                  : undefined;
        markReconnecting(recoveryDelay);
    }, [disableStreams, meetingClient, markReconnecting, roomId, mobileOS, tryPreserveSessionDespiteSignalingLoss]);

    // Escuta o estado de conexão do Dyte (socket + mídia). Em `disconnected`/`failed`
    // age ANTES do roomLeft — tenta curar a sessão cedo em vez de esperar cair.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
        const healDelayDisconnected = isMobileDevice
            ? SOCKET_DISCONNECTED_GRACE_MS_MOBILE
            : SOCKET_DISCONNECTED_GRACE_MS_DESKTOP;
        const healDelayReconnecting = isMobileDevice
            ? SOCKET_RECONNECTING_GRACE_MS_MOBILE
            : SOCKET_RECONNECTING_GRACE_MS_DESKTOP;
        const poorConnectionStrikesRef = { current: 0 };

        const handleSocketUpdate = (payload: {
            state?: string;
            reconnectionAttempt?: number;
            reconnected?: boolean;
        }) => {
            if (payload?.reconnected) {
                sdkSocketReconnectAttemptRef.current = 0;
                poorConnectionStrikesRef.current = 0;
                markConnected();
                return;
            }

            const state = payload?.state;
            if (state === "connected") {
                sdkSocketReconnectAttemptRef.current = 0;
                poorConnectionStrikesRef.current = 0;
                markConnected();
            } else if (state === "reconnecting") {
                const attempt = payload.reconnectionAttempt ?? 0;
                sdkSocketReconnectAttemptRef.current = attempt;
                if (attempt > 0 && attempt < SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS) {
                    cancelConnectionRecovery();
                    setConnectionStatus("reconnecting");
                    if (!wasReconnectingRef.current) {
                        wasReconnectingRef.current = true;
                        logConnRef.current("reconnecting", {
                            socketReconnectionAttempt: attempt,
                        });
                    }
                    return;
                }
                if (tryPreserveSessionDespiteSignalingLoss()) {
                    return;
                }
                markReconnecting(healDelayReconnecting);
            } else if (state === "disconnected" || state === "failed") {
                const attempt = sdkSocketReconnectAttemptRef.current;
                if (attempt > 0 && attempt < SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS) {
                    cancelConnectionRecovery();
                    setConnectionStatus("reconnecting");
                    return;
                }
                if (tryPreserveSessionDespiteSignalingLoss()) {
                    return;
                }
                markReconnecting(healDelayDisconnected);
            }
        };

        const handleMediaUpdate = (payload: { state?: string }) => {
            const state = payload?.state;
            const socketState = meetingClient.meta?.socketState?.state;
            const nativeAttempt = sdkSocketReconnectAttemptRef.current;
            const sdkRecovering =
                socketState === "reconnecting" &&
                nativeAttempt > 0 &&
                nativeAttempt < SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS;

            if (state === "connected") {
                if (socketState === "connected") {
                    poorConnectionStrikesRef.current = 0;
                    markConnected();
                }
            } else if (state === "reconnecting") {
                if (!sdkRecovering) {
                    if (tryPreserveSessionDespiteSignalingLoss()) {
                        return;
                    }
                    markReconnecting(healDelayReconnecting);
                }
            } else if (state === "disconnected" || state === "failed") {
                if (!sdkRecovering && socketState !== "reconnecting") {
                    if (tryPreserveSessionDespiteSignalingLoss()) {
                        return;
                    }
                    markReconnecting(healDelayDisconnected);
                }
            }
        };

        const handleRoomJoined = (payload: { reconnected?: boolean }) => {
            if (payload?.reconnected) {
                markConnected();
            }

            const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
            if (isMobileDevice) {
                const applyMobileAudio = () => {
                    void ensureMobileSpeakerOutput(meetingClient);
                    boostRemoteMediaElements();
                };
                applyMobileAudio();
                window.setTimeout(applyMobileAudio, 400);
                window.setTimeout(applyMobileAudio, 2000);
            }
        };

        // Conexão fraca: o Dyte sinaliza qualidade ruim. Reduzimos a resolução do
        // próprio vídeo para estabilizar a chamada (menos banda = menos quedas) e
        // exibimos um indicador DISCRETO e TEMPORÁRIO. O aviso some sozinho quando
        // a conexão para de sinalizar problema (debounce) — antes ele ficava preso
        // para sempre após o primeiro sinal, aparecendo pra todo mundo no celular.
        const clearPoorConnectionState = () => {
            poorConnectionTimerRef.current = null;
            setIsPoorConnection(false);
            lowVideoAppliedRef.current = false;
            if (videoOffForNetworkRef.current) {
                return;
            }
            const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
            const defaults = joinVideoDefaults(isMobileDevice);
            try {
                void meetingClient.self.updateVideoConstraints({
                    width: { ideal: defaults.width },
                    height: { ideal: defaults.height },
                    frameRate: { ideal: defaults.frameRate },
                });
            } catch (restoreErr) {
                console.warn("Falha ao restaurar qualidade de vídeo:", restoreErr);
            }
        };

        const handlePoorConnection = () => {
            setIsPoorConnection(true);
            poorConnectionStrikesRef.current += 1;

            if (poorConnectionTimerRef.current !== null) {
                window.clearTimeout(poorConnectionTimerRef.current);
            }
            poorConnectionTimerRef.current = window.setTimeout(clearPoorConnectionState, 12000);

            const snapshot = evaluateNetworkQuality();
            const tier = snapshot?.tier ?? "fair";
            const shouldAudioOnly =
                tier === "poor" ||
                poorConnectionStrikesRef.current >= 2 ||
                (isMobileDevice && tier === "fair");

            if (shouldAudioOnly && meetingClient.self.videoEnabled) {
                hadVideoBeforeNetworkOffRef.current = true;
                videoOffForNetworkRef.current = true;
                lowVideoAppliedRef.current = true;
                void Promise.resolve(meetingClient.self.disableVideo()).catch(() => {});
                return;
            }

            if (lowVideoAppliedRef.current) {
                return;
            }
            lowVideoAppliedRef.current = true;
            try {
                const fair = videoConstraintsForTier("fair");
                void meetingClient.self.updateVideoConstraints({
                    width: { ideal: fair.width },
                    height: { ideal: fair.height },
                    frameRate: { ideal: fair.frameRate },
                });
            } catch (qualityErr) {
                console.warn("Falha ao reduzir qualidade de vídeo:", qualityErr);
            }
        };

        // Erro de permissão de mídia (câmera/microfone) — comum em mobile.
        const handleMediaPermissionError = (payload: { kind?: string }) => {
            const kind = payload?.kind === "audio" ? "microfone" : payload?.kind === "video" ? "câmera" : "câmera/microfone";
            Swal.fire({
                icon: "warning",
                title: `Não foi possível acessar ${kind}`,
                text: `Verifique as permissões de ${kind} do navegador e tente novamente. Em celular, confira também as permissões do app/navegador nas configurações do sistema.`,
            });
        };

        // Erro de autoplay: o navegador bloqueou o áudio até interação do usuário.
        const handleAutoplayError = () => {
            const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
            Swal.fire({
                icon: "info",
                title: "Toque para ativar o áudio",
                text: "Seu navegador bloqueou a reprodução automática. Toque na tela para ouvir os demais participantes.",
                timer: 5000,
                showConfirmButton: true,
                confirmButtonText: "Ativar áudio",
            }).then(() => {
                if (isMobileDevice) {
                    void ensureMobileSpeakerOutput(meetingClient, {
                        iosMicPulse: mobileOS === "ios",
                    });
                } else {
                    boostRemoteMediaElements();
                }
            });
        };

        meetingClient.meta.on("socketConnectionUpdate", handleSocketUpdate);
        meetingClient.meta.on("mediaConnectionUpdate", handleMediaUpdate);
        meetingClient.meta.on("poorConnection", handlePoorConnection);
        meetingClient.self.on("roomJoined", handleRoomJoined);
        meetingClient.self.on("mediaPermissionError", handleMediaPermissionError);
        meetingClient.self.on("autoplayError", handleAutoplayError);

        return () => {
            if (poorConnectionTimerRef.current !== null) {
                window.clearTimeout(poorConnectionTimerRef.current);
                poorConnectionTimerRef.current = null;
            }
            meetingClient.meta.removeListener("socketConnectionUpdate", handleSocketUpdate);
            meetingClient.meta.removeListener("mediaConnectionUpdate", handleMediaUpdate);
            meetingClient.meta.removeListener("poorConnection", handlePoorConnection);
            meetingClient.self.removeListener("roomJoined", handleRoomJoined);
            meetingClient.self.removeListener("mediaPermissionError", handleMediaPermissionError);
            meetingClient.self.removeListener("autoplayError", handleAutoplayError);
        };
    }, [meetingClient, markConnected, markReconnecting, mobileOS, defaultRecoveryDelayMs, cancelConnectionRecovery, tryPreserveSessionDespiteSignalingLoss]);

    // Salas com 4+ participantes: reduz resolução de envio (menos uplink agregado).
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";

        const countJoined = (): number => {
            try {
                return meetingClient.participants.joined.toArray().length + 1;
            } catch {
                return 1;
            }
        };

        const applyGroupVideoProfile = () => {
            if (intentionalLeaveRef.current || videoOffForNetworkRef.current) {
                return;
            }

            const count = countJoined();
            if (count < 4) {
                if (!groupVideoAppliedRef.current) {
                    return;
                }
                groupVideoAppliedRef.current = false;
                lowVideoAppliedRef.current = false;
                const defaults = joinVideoDefaults(isMobileDevice);
                try {
                    void meetingClient.self.updateVideoConstraints({
                        width: { ideal: defaults.width },
                        height: { ideal: defaults.height },
                        frameRate: { ideal: defaults.frameRate },
                    });
                } catch (restoreErr) {
                    console.warn("Falha ao restaurar vídeo após queda de participantes:", restoreErr);
                }
                return;
            }

            const profile = videoConstraintsForParticipantCount(count, isMobileDevice);
            groupVideoAppliedRef.current = true;
            lowVideoAppliedRef.current = true;
            try {
                void meetingClient.self.updateVideoConstraints({
                    width: { ideal: profile.width },
                    height: { ideal: profile.height },
                    frameRate: { ideal: profile.frameRate },
                });
            } catch (qualityErr) {
                console.warn("Falha ao reduzir vídeo para tamanho do grupo:", qualityErr);
            }
        };

        applyGroupVideoProfile();

        const onParticipantChange = () => applyGroupVideoProfile();
        meetingClient.participants.joined.on("participantJoined", onParticipantChange);
        meetingClient.participants.joined.on("participantLeft", onParticipantChange);

        return () => {
            meetingClient.participants.joined.removeListener("participantJoined", onParticipantChange);
            meetingClient.participants.joined.removeListener("participantLeft", onParticipantChange);
        };
    }, [meetingClient, mobileOS]);

    // Segundo plano (celular): ao voltar para a aba/app, se o socket do Dyte
    // não sobreviveu, sinaliza reconexão. No celular disparamos recuperação
    // imediata (sem esperar 12s) — caso típico de queda ao trocar de app.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";

        const handleVisibilityHidden = () => {
            if (document.visibilityState === "hidden") {
                wasPageHiddenRef.current = true;
                logConnRef.current("visibilityHidden");
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState !== "visible") {
                return;
            }

            const returnedFromBackground = wasPageHiddenRef.current;
            wasPageHiddenRef.current = false;

            if (returnedFromBackground) {
                markBackgroundReturn();
                resumedFromBackgroundRef.current = true;
                logConnRef.current("visibilityResume");
                showStayInForegroundToast(lastForegroundToastAtRef);
            }

            const socketConnected = meetingClient.meta?.socketState?.state === "connected";
            if (socketConnected) {
                return;
            }

            // Socket caído mas mídia ainda ok: espera o SDK — não soft-reconnect cedo.
            if (
                shouldWaitForSdkAfterVisibilityResume({
                    socketConnected: false,
                    mediaTransportHealthy: isMediaTransportHealthy(lastWebRtcSnapshotRef.current),
                    meetingDocExists: meetingDocExistsRef.current,
                })
            ) {
                if (!intentionalLeaveRef.current) {
                    setConnectionStatus("reconnecting");
                }
                markReconnecting(
                    isMobileDevice
                        ? SOCKET_RECONNECTING_GRACE_MS_MOBILE
                        : SOCKET_RECONNECTING_GRACE_MS_DESKTOP,
                );
                return;
            }

            if (!intentionalLeaveRef.current) {
                setConnectionStatus("reconnecting");
            }

            // Dá tempo ao SDK reconectar sozinho ao voltar do segundo plano.
            // Rejoin imediato (400ms) estava derrubando a sessão e mandando ao lobby.
            const resumeDelayMs = isMobileDevice ? 12000 : 18000;
            markReconnecting(resumeDelayMs);
        };

        document.addEventListener("visibilitychange", handleVisibilityHidden);
        document.addEventListener("visibilitychange", handleVisibility);
        window.addEventListener("pageshow", handleVisibility);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityHidden);
            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("pageshow", handleVisibility);
        };
    }, [meetingClient, mobileOS, markReconnecting]);

    // Page Lifecycle API (Chrome/Android): abas congeladas em segundo plano podem
    // matar WebRTC sem disparar visibilitychange. No resume, verificamos saúde
    // do socket e reconectamos se necessário — complementa visibilitychange.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";

        const checkConnectionHealth = () => {
            if (intentionalLeaveRef.current) {
                return;
            }

            const socketState = meetingClient.meta?.socketState?.state;
            const mediaStateRaw = meetingClient.meta?.mediaState as
                | string
                | { state?: string }
                | undefined;
            const mediaState =
                typeof mediaStateRaw === "string" ? mediaStateRaw : mediaStateRaw?.state;
            const socketOk = socketState === "connected";
            const mediaOk = mediaState === "connected" || mediaState === "reconnecting";
            const webrtcOk = isMediaTransportHealthy(lastWebRtcSnapshotRef.current);

            if (socketOk && (mediaOk || webrtcOk)) {
                return;
            }

            // Signaling ruim, mídia ainda viva: grace longo do SDK, sem leave agressivo.
            if (
                !socketOk &&
                shouldWaitForSdkAfterVisibilityResume({
                    socketConnected: false,
                    mediaTransportHealthy: webrtcOk || mediaOk,
                    meetingDocExists: meetingDocExistsRef.current,
                })
            ) {
                if (!intentionalLeaveRef.current) {
                    setConnectionStatus("reconnecting");
                }
                markReconnecting(
                    isMobileDevice
                        ? SOCKET_RECONNECTING_GRACE_MS_MOBILE
                        : SOCKET_RECONNECTING_GRACE_MS_DESKTOP,
                );
                return;
            }

            if (!socketOk) {
                if (!intentionalLeaveRef.current) {
                    setConnectionStatus("reconnecting");
                }
                markReconnecting(isMobileDevice ? 12000 : 18000);
                return;
            }

            if (!mediaOk && !webrtcOk) {
                markReconnecting(isMobileDevice ? 12000 : 18000);
            }
        };

        const handleFreeze = () => {
            wasPageHiddenRef.current = true;
            logConnRef.current("pageFreeze");
            cancelConnectionRecovery();
        };

        const handleResume = () => {
            markBackgroundReturn();
            resumedFromBackgroundRef.current = true;
            logConnRef.current("pageResume");
            window.setTimeout(() => checkConnectionHealth(), 400);
        };

        document.addEventListener("freeze", handleFreeze);
        document.addEventListener("resume", handleResume);

        return () => {
            document.removeEventListener("freeze", handleFreeze);
            document.removeEventListener("resume", handleResume);
        };
    }, [meetingClient, mobileOS, cancelConnectionRecovery, markReconnecting]);

    // Rede 2G/3G: aviso persistente + redução proativa de vídeo (antes do SDK
    // sinalizar poorConnection — Auxiliadora em slow-2g caía com vídeo alto).
    useEffect(() => {
        if (!meetingClient?.self) {
            return;
        }

        const applyTierVideo = (tier: NetworkTier) => {
            if (networkTierAppliedRef.current === tier) {
                return;
            }
            networkTierAppliedRef.current = tier;

            const profile = videoConstraintsForTier(tier);

            if (tier === "good") {
                if (videoOffForNetworkRef.current && hadVideoBeforeNetworkOffRef.current) {
                    videoOffForNetworkRef.current = false;
                    hadVideoBeforeNetworkOffRef.current = false;
                    void enableVideoAndRestoreBackground(meetingClient).then((result) => {
                        if (result.suggestManualRestore) {
                            showVirtualBackgroundRestoreTip(lastVbRestoreToastAtRef);
                        }
                    });
                }
                lowVideoAppliedRef.current = false;
                return;
            }

            try {
                if (profile.audioOnly) {
                    if (meetingClient.self.videoEnabled) {
                        hadVideoBeforeNetworkOffRef.current = true;
                        videoOffForNetworkRef.current = true;
                        void Promise.resolve(meetingClient.self.disableVideo()).catch(() => {});
                    }
                    lowVideoAppliedRef.current = true;
                    return;
                }

                videoOffForNetworkRef.current = false;
                void meetingClient.self.updateVideoConstraints({
                    width: { ideal: profile.width },
                    height: { ideal: profile.height },
                    frameRate: { ideal: profile.frameRate },
                });
                lowVideoAppliedRef.current = true;
            } catch (err) {
                console.warn("Falha ao ajustar vídeo para rede lenta:", err);
            }
        };

        const refreshNetworkState = () => {
            const snapshot = evaluateNetworkQuality();
            if (!snapshot) {
                setSlowNetworkWarning(null);
                return;
            }
            setSlowNetworkWarning(networkWarningMessage(snapshot));
            if (snapshot.tier !== "good") {
                applyTierVideo(snapshot.tier);
            }
        };

        refreshNetworkState();
        return subscribeNetworkChanges(refreshNetworkState);
    }, [meetingClient]);

    // Captura middlewares de fundo virtual aplicados na UI do RTK.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }
        return installVirtualBackgroundCapture(meetingClient);
    }, [meetingClient]);

    // Pausa o VÍDEO DE ENVIO em segundo plano só no mobile. No desktop, desligar
    // a câmera ao Alt-Tab derrubava o fundo virtual e aumentava quedas (caso Erica).
    // Debounce 1,5s: troca rápida de app/notificação não desarma a câmera.
    useEffect(() => {
        if (!meetingClient?.self) {
            return;
        }

        const isMobileDevice = mobileOS === "android" || mobileOS === "ios";
        if (!isMobileDevice) {
            return;
        }

        const clearPauseDebounce = () => {
            if (videoPauseDebounceRef.current !== null) {
                window.clearTimeout(videoPauseDebounceRef.current);
                videoPauseDebounceRef.current = null;
            }
        };

        const pauseVideoForBackground = () => {
            if (intentionalLeaveRef.current) {
                return;
            }
            if (meetingClient.self.videoEnabled) {
                videoWasOnBeforeHideRef.current = true;
                void Promise.resolve(meetingClient.self.disableVideo()).catch(() => {});
            } else {
                videoWasOnBeforeHideRef.current = false;
            }
        };

        const schedulePauseVideoForBackground = () => {
            clearPauseDebounce();
            videoPauseDebounceRef.current = window.setTimeout(() => {
                videoPauseDebounceRef.current = null;
                if (document.visibilityState === "hidden") {
                    pauseVideoForBackground();
                }
            }, 1500);
        };

        const resumeVideoOnForeground = () => {
            clearPauseDebounce();
            if (intentionalLeaveRef.current || videoOffForNetworkRef.current) {
                return;
            }
            if (videoWasOnBeforeHideRef.current && !meetingClient.self.videoEnabled) {
                void enableVideoAndRestoreBackground(meetingClient).then((result) => {
                    if (result.suggestManualRestore) {
                        showVirtualBackgroundRestoreTip(lastVbRestoreToastAtRef);
                    }
                });
            }
            videoWasOnBeforeHideRef.current = false;
        };

        const handleVideoVisibility = () => {
            if (document.visibilityState === "hidden") {
                schedulePauseVideoForBackground();
            } else if (document.visibilityState === "visible") {
                resumeVideoOnForeground();
            }
        };

        document.addEventListener("visibilitychange", handleVideoVisibility);
        window.addEventListener("pageshow", resumeVideoOnForeground);
        window.addEventListener("pagehide", schedulePauseVideoForBackground);

        return () => {
            clearPauseDebounce();
            document.removeEventListener("visibilitychange", handleVideoVisibility);
            window.removeEventListener("pageshow", resumeVideoOnForeground);
            window.removeEventListener("pagehide", schedulePauseVideoForBackground);
        };
    }, [meetingClient, mobileOS]);

    // Limpa o timer de fallback ao desmontar.
    useEffect(() => cancelConnectionRecovery, [cancelConnectionRecovery]);

    // Aviso ao tentar fechar a aba, recarregar ou trocar de URL durante a
    // sessão (vale para TODOS — moderador e participantes). Objetivo: o sistema
    // não confundir uma SAÍDA VOLUNTÁRIA com uma QUEDA de conexão. As saídas que
    // o próprio app dispara (botão "Sair", ser expulso/encerrado pelo moderador,
    // ou o reload automático de recuperação) NÃO mostram o aviso.
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (intentionalLeaveRef.current || programmaticNavRef.current) {
                return;
            }
            event.preventDefault();
            // Navegadores modernos ignoram o texto e exibem uma mensagem padrão,
            // mas exigem returnValue definido para exibir o prompt de confirmação.
            event.returnValue = "";
            return "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    // Wake Lock: mantém a tela ligada durante a sessão para evitar que o celular
    // bloqueie sozinho e suspenda a conexão (causa comum de "sumiço" em sessões
    // longas). Reaquire ao voltar do segundo plano. Degrada silenciosamente em
    // navegadores sem suporte.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }

        type WakeLockSentinelLike = { release: () => Promise<void> };
        const wakeLockApi = (navigator as unknown as {
            wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
        }).wakeLock;

        if (!wakeLockApi) {
            return;
        }

        let sentinel: WakeLockSentinelLike | null = null;
        let released = false;

        const requestWakeLock = async () => {
            try {
                sentinel = await wakeLockApi.request("screen");
            } catch (err) {
                // Sem suporte / negado: ignora.
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === "visible" && !released) {
                void requestWakeLock();
            }
        };

        void requestWakeLock();
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            released = true;
            document.removeEventListener("visibilitychange", handleVisibility);
            if (sentinel) {
                void sentinel.release().catch(() => undefined);
            }
        };
    }, [meetingClient]);

    // Keep-alive de segundo plano: mantém um áudio inaudível tocando enquanto o
    // usuário está na sala. Navegadores de celular não suspendem abas que estão
    // "tocando áudio", então isso impede que a página seja congelada em segundo
    // plano (troca de app / tela bloqueada) — principal causa de quedas no
    // celular. Diferente do Wake Lock (que só vale com a tela visível), este
    // atua justamente quando o app vai para segundo plano.
    useEffect(() => {
        if (!meetingClient) {
            return;
        }
        const keepAlive = startBackgroundKeepAlive();
        return () => keepAlive.stop();
    }, [meetingClient]);

    // Mobile: manter saída no alto-falante (evita áudio baixo no auricular na aba Cenário).
    useEffect(() => {
        if (!meetingClient || !isMobile) {
            return;
        }

        const apply = () => {
            void ensureMobileSpeakerOutput(meetingClient);
            boostRemoteMediaElements();
        };

        apply();

        const onVisible = () => {
            if (document.visibilityState === "visible") {
                apply();
            }
        };
        document.addEventListener("visibilitychange", onVisible);

        const intervalId = window.setInterval(() => {
            if (document.visibilityState !== "visible") {
                return;
            }
            apply();
        }, 25000);

        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.clearInterval(intervalId);
        };
    }, [meetingClient, isMobile, activeTab]);

    // Novos participantes / faixas de áudio remotas: reforça reprodução no mobile.
    useEffect(() => {
        if (!meetingClient || !isMobile) {
            return;
        }

        const joined = meetingClient.participants?.joined;
        if (!joined?.on) {
            return;
        }

        const onRemoteAudio = () => {
            void ensureMobileSpeakerOutput(meetingClient);
            boostRemoteMediaElements();
        };

        joined.on("participantJoined", onRemoteAudio);
        joined.on("audioUpdate", onRemoteAudio);

        return () => {
            joined.removeListener("participantJoined", onRemoteAudio);
            joined.off?.("audioUpdate", onRemoteAudio);
        };
    }, [meetingClient, isMobile]);

    useEffect(() => {
        if (!meetingClient) return;

        if (!isModerator) {
            setAccessRequests([]);
            previousPendingRequestsCount.current = 0;
            hasInitializedRequests.current = false;
            return;
        }

        const requestsRef = collection(db, "dyteMeetings", roomId, "accessRequests");
        const pendingQuery = query(requestsRef, where("status", "==", "pending"), orderBy("requestedAt", "asc"));

        const unsubscribe = onSnapshot(pendingQuery, (snapshot) => {
            const pendingRequests: RoomAccessRequest[] = snapshot.docs.map((docSnapshot) => {
                const data = docSnapshot.data() as Record<string, any>;
                return {
                    id: docSnapshot.id,
                    userId: data.userId as string,
                    status: data.status ?? "pending",
                    name: data.name ?? null,
                    email: data.email ?? null,
                    sessionIdentityKey: data.sessionIdentityKey ?? null,
                    requestedAt: data.requestedAt?.toDate ? data.requestedAt.toDate() : null,
                } as RoomAccessRequest;
            });

            // Convidados anônimos geram um uid novo por aba/sessão, então a mesma
            // pessoa pode aparecer duplicada. Colapsamos pelos mesmos critérios de
            // identidade (sessionIdentityKey > email > nome), mantendo o pedido mais
            // recente. Pedidos antigos sem chave continuam sendo deduplicados por nome.
            const dedupedByIdentity = new Map<string, RoomAccessRequest>();
            for (const request of pendingRequests) {
                const identityKey = (
                    request.sessionIdentityKey ||
                    request.email ||
                    request.name ||
                    request.userId
                )
                    .toString()
                    .trim()
                    .toLowerCase();

                const existing = dedupedByIdentity.get(identityKey);
                if (!existing) {
                    dedupedByIdentity.set(identityKey, request);
                    continue;
                }

                const existingTime = existing.requestedAt?.getTime?.() ?? 0;
                const currentTime = request.requestedAt?.getTime?.() ?? 0;
                if (currentTime >= existingTime) {
                    dedupedByIdentity.set(identityKey, request);
                }
            }

            const uniqueRequests = Array.from(dedupedByIdentity.values()).sort(
                (a, b) => (a.requestedAt?.getTime?.() ?? 0) - (b.requestedAt?.getTime?.() ?? 0),
            );

            setAccessRequests(uniqueRequests);
        }, (error) => {
            console.error("Erro ao ouvir solicitações de acesso:", error);
        });

        return () => unsubscribe();
    }, [roomId, isModerator, meetingClient]);

    useEffect(() => {
        if (!isModerator) {
            return;
        }

        if (!hasInitializedRequests.current) {
            hasInitializedRequests.current = true;
        } else if (accessRequests.length > previousPendingRequestsCount.current) {
            const newRequest = accessRequests[accessRequests.length - 1];
            const userName = newRequest?.name || newRequest?.email || "Um participante";

            playAccessRequestSound();

            const toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 4000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener("mouseenter", Swal.stopTimer);
                    toast.addEventListener("mouseleave", Swal.resumeTimer);
                }
            });

            toast.fire({
                icon: "info",
                title: "Novo pedido de acesso",
                text: `${userName} solicitou entrada na sala.`
            });
        }

        previousPendingRequestsCount.current = accessRequests.length;
    }, [accessRequests, isModerator]);

    // Monitorar saldo de créditos e alertar quando estiver baixo
    useEffect(() => {
        if (!isOwner || !isLowBalance || hasShownLowCreditAlert.current) {
            return;
        }

        hasShownLowCreditAlert.current = true;

        Swal.fire({
            icon: "warning",
            title: "Créditos em horas baixos",
            html: `
                <p class="mb-2">Suas horas de terapia estão acabando!</p>
                <p class="mb-2">Saldo atual: <strong>${Math.round((hoursBalance || 0) * 60)} minutos</strong></p>
                <p class="text-sm text-gray-600">Faça uma recarga para continuar atendendo seus pacientes sem interrupções.</p>
            `,
            confirmButtonText: "Recarregar agora",
            showCancelButton: true,
            cancelButtonText: "Depois",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#6b7280",
        }).then((result) => {
            if (result.isConfirmed) {
                window.open(toMarketingUrl("/planos?secao=terapeuta"), "_blank");
            }
        });
    }, [isLowBalance, hoursBalance, isOwner]);

    const handleRespondToAccessRequest = useCallback(async (requestId: string, action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY) => {
        if (!isModerator) {
            return;
        }

        setProcessingRequestId(requestId);

        try {
            const respond = httpsCallable<
                { roomId: string; requestId: string; action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY },
                { success: boolean; message?: string }
            >(functions, "respondToRoomAccessRequest");

            await respond({
                roomId,
                requestId,
                action,
            });
        } catch (error) {
            console.error("Erro ao responder solicitação de acesso:", error);
            Swal.fire({
                icon: "error",
                title: "Não foi possível processar o pedido",
                text: "Tente novamente em instantes.",
            });
        } finally {
            setProcessingRequestId(null);
        }
    }, [functions, roomId, isModerator]);

    // Promove/remove um co-admin. Qualquer moderador (dono ou co-admin) pode usar;
    // o backend valida a permissão e impede remover o dono.
    const handleSetModerator = useCallback(async (targetUserId: string, makeModerator: boolean) => {
        if (!isModerator || !roomId) return;
        try {
            const setMod = httpsCallable<
                { roomId: string; targetUserId: string; makeModerator: boolean },
                { success: boolean; message?: string }
            >(functions, "setRoomModerator");
            await setMod({ roomId, targetUserId, makeModerator });
        } catch (error) {
            console.error("Erro ao atualizar administrador da sala:", error);
            Swal.fire({
                icon: "error",
                title: "Não foi possível atualizar",
                text: "Não foi possível alterar o administrador. Tente novamente.",
            });
        }
    }, [functions, roomId, isModerator]);

    const dyteMeetingComponent = useMemo(() => {
        return (
            <>
                {meetingClient ? (
                    <DyteMeet
                        key={`dyte-${roomId}-${user?.uid}`}
                        meeting={meetingClient}
                        onMeetingEnd={handleDyteRoomLeft}
                    />
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-white space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                        <span>{loadingMessage || "Carregando videochamada..."}</span>
                    </div>
                )}
            </>
        )
    }, [meetingClient, loadingMessage, handleDyteRoomLeft, roomId, user?.uid]);

    const mobileTabButtonClass = (tab: "scene" | "video") =>
        `flex-1 py-2 px-2 text-xs font-medium flex items-center justify-center space-x-2 transition-colors ${
            activeTab === tab
                ? "bg-blue-600 text-white"
                : "text-gray-300 hover:bg-gray-700"
        }`;

    return (
        <div className="meeting-room-shell bg-slate-900 flex flex-col overflow-hidden">
            {connectionStatus === "reconnecting" && (
                <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-slate-900/85 backdrop-blur-sm px-6 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mb-4"></div>
                    <h3 className="text-lg font-semibold text-white mb-1">Reconectando…</h3>
                    <p className="max-w-xs text-sm text-blue-100/90">
                        Sua conexão oscilou (ou o app ficou em segundo plano). Estamos te reconectando à sala automaticamente — mantenha esta tela aberta.
                    </p>
                </div>
            )}
            {meetingClient && connectionStatus === "connected" && slowNetworkWarning && (
                <div className="fixed top-[calc(0.5rem+env(safe-area-inset-top,0px))] left-1/2 z-[91] w-[min(92vw,22rem)] -translate-x-1/2 rounded-lg bg-orange-600/95 px-3 py-2 text-center text-[11px] font-medium leading-snug text-white shadow-lg">
                    {slowNetworkWarning}
                </div>
            )}
            {meetingClient && connectionStatus === "connected" && isPoorConnection && (
                <div className={`fixed left-1/2 z-[90] -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-amber-500/95 px-3 py-1 text-[11px] font-semibold text-white shadow-lg ${slowNetworkWarning ? "top-[calc(3.25rem+env(safe-area-inset-top,0px))]" : "top-[calc(0.5rem+env(safe-area-inset-top,0px))]"}`}>
                    <Wifi className="h-3.5 w-3.5" />
                    Conexão instável — qualidade reduzida
                </div>
            )}
            <MicDisabledBanner
                meetingClient={meetingClient}
                hideForObserver={isObserverInSession}
            />
            <RoomOnboardingTour
                isModerator={isModerator}
                isMobile={isMobile}
                forceOpen={forceRoomTour}
                onForceOpenHandled={() => setForceRoomTour(false)}
                onActivateTab={(tab) => setActiveTab(tab)}
            />
            <ObservatorHandler roomId={roomId} user={user} />
            <InviteUserModal
                showInviteModal={showInviteModal}
                setShowInviteModal={setShowInviteModal}
                roomId={roomId}
                user={user}
            />

            <ParticipantsPanelModal
                meetingClient={meetingClient}
                roomId={roomId}
                user={user}
                sessionSetting={{ isRoomModerator: isModerator }}
                isOwner={isOwner}
                moderators={liveModerators}
                onSetModerator={handleSetModerator}
                isPanelOpen={isPanelOpen}
                setIsPanelOpen={setIsPanelOpen}
                accessRequests={accessRequests}
                onRespondToAccessRequest={handleRespondToAccessRequest}
                processingRequestId={processingRequestId}
            />

            <VirtualAvatarModal
                isOpen={isVirtualAvatarModalOpen}
                onClose={() => setIsVirtualAvatarModalOpen(false)}
                onConfirm={handleCreateVirtualAvatar}
            />
            <SceneObjectModal
                isOpen={isSceneObjectModalOpen}
                onClose={() => setIsSceneObjectModalOpen(false)}
                onConfirm={handleCreateSceneObject}
            />


            <div className="flex flex-1 min-h-0">
                {isMobile ? (
                /* Mobile — um único DyteMeet (evita telemetria duplicada) */
                <div className="flex-1 flex flex-col min-h-0">
                    {!isPortrait && (
                        <header className="flex-shrink-0 bg-gray-800/95 backdrop-blur-md border-b border-gray-700/50 z-10 flex">
                            <button
                                onClick={() => setActiveTab("scene")}
                                className={mobileTabButtonClass("scene")}
                            >
                                <UserIcon className="w-4 h-4" /> <span>Cenário</span>
                            </button>
                            <button
                                onClick={() => setActiveTab("video")}
                                className={mobileTabButtonClass("video")}
                            >
                                <Video className="w-4 h-4" /> <span>Videochamada</span>
                            </button>
                        </header>
                    )}

                    <main className="flex-1 min-h-0 relative">
                        {isPortrait && (
                            <div className="absolute top-[calc(0.5rem+env(safe-area-inset-top,0px))] left-3 right-3 z-30 flex rounded-full bg-gray-900/85 backdrop-blur-md border border-gray-700/50 p-0.5 shadow-lg">
                                <button
                                    onClick={() => setActiveTab("scene")}
                                    className={`${mobileTabButtonClass("scene")} rounded-full`}
                                >
                                    <UserIcon className="w-4 h-4" /> <span>Cenário</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab("video")}
                                    className={`${mobileTabButtonClass("video")} rounded-full`}
                                >
                                    <Video className="w-4 h-4" /> <span>Videochamada</span>
                                </button>
                            </div>
                        )}

                        {/* Modal de dica de orientação (opcional, aberto pelo usuário) */}
                        {isPortrait && showOrientationModal && (
                            <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-5">
                                <div className="w-full max-w-xs rounded-2xl bg-slate-800 border border-blue-400/30 shadow-2xl p-6 flex flex-col items-center text-center">
                                    {/* Ícone animado */}
                                    <div className="relative mb-3 flex h-14 w-14 items-center justify-center rounded-full border-2 border-blue-400/50 bg-blue-500/20">
                                        <Smartphone
                                            className={`h-7 w-7 text-blue-200 transition-transform duration-700 ${isRotationIconHorizontal ? "rotate-90" : "rotate-0"}`}
                                        />
                                        <RotateCw className="absolute -right-1 -bottom-1 h-4 w-4 text-blue-300 animate-spin" />
                                    </div>

                                    <h3 className="text-base font-bold text-white mb-1">Gire o celular para uma experiência melhor</h3>
                                    <p className="text-sm text-blue-100/90 mb-3">
                                        O cenário funciona na vertical, mas na horizontal você enxerga os avatares com mais espaço.
                                    </p>

                                    {/* Foto do dispositivo com instrução visual */}
                                    {(mobileOS === "ios" || mobileOS === "android") && (
                                        <div className="w-full mb-3 rounded-xl overflow-hidden border border-blue-400/20">
                                            <img
                                                src={mobileOS === "ios" ? "/assets/desbloqueio/ios.gif" : "/assets/desbloqueio/android.gif"}
                                                alt={mobileOS === "ios" ? "Como desativar o bloqueio de rotação no iPhone" : "Como ativar rotação automática no Android"}
                                                className="w-full object-contain max-h-56"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}

                                    <p className="text-xs text-blue-200/80 mb-4">
                                        {mobileOS === "ios"
                                            ? "Abra a Central de Controle (arraste de cima para baixo) e toque no ícone de cadeado com a seta circular para desativar o bloqueio de rotação."
                                            : mobileOS === "android"
                                                ? "Abra os atalhos rápidos (arraste de cima para baixo duas vezes) e toque em Rotação Automática para ativá-la."
                                                : "Desative o bloqueio de rotação nas configurações rápidas do celular."}
                                    </p>

                                    <button
                                        type="button"
                                        onClick={() => { setShowOrientationModal(false); }}
                                        className="w-full rounded-full bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
                                    >
                                        Entendi
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Dica discreta no canto (não bloqueia o cenário) */}
                        {isPortrait && activeTab === "scene" && !showOrientationModal && (
                            <button
                                type="button"
                                onClick={() => setShowOrientationModal(true)}
                                className="absolute top-[calc(3.75rem+env(safe-area-inset-top,0px))] right-3 z-30 flex items-center gap-1.5 rounded-full bg-blue-600/90 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm"
                            >
                                <Smartphone className={`h-3.5 w-3.5 transition-transform duration-700 ${isRotationIconHorizontal ? "rotate-90" : "rotate-0"}`} />
                                Girar celular
                            </button>
                        )}

                        {isPortrait && activeTab === "scene" && meetingClient && (
                            <button
                                type="button"
                                onClick={() => setActiveTab("video")}
                                className="absolute bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] left-3 z-30 flex items-center gap-1.5 rounded-full bg-gray-900/85 px-3 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur-sm border border-gray-700/50"
                                aria-label="Abrir videochamada"
                            >
                                <Video className="h-4 w-4 text-blue-400" />
                                Videochamada
                            </button>
                        )}

                        <MobileLowAudioBanner
                            meetingClient={meetingClient}
                            mobileOS={mobileOS}
                            showOnSceneTab={activeTab === "scene" && sessionJoined}
                        />

                        <div
                            data-tour="scene"
                            className={`absolute inset-0 h-full w-full mobile-scene-layer ${activeTab === "scene" ? "block" : "hidden"}`}
                        >
                            <Scene
                                roomId={roomId}
                                onError={handleSceneError}
                                sessionSetting={
                                    sessionSetting
                                        ? {
                                            ...sessionSetting,
                                            isRoomModerator: isModerator,
                                            isRoomOwner: isOwner,
                                        }
                                        : sessionSetting
                                }
                                meetingClient={meetingClient}
                                layoutMode={layoutMode}
                            />
                        </div>
                        <div
                            data-tour="video"
                            className={
                                activeTab === "video"
                                    ? "absolute inset-0 h-full w-full z-10"
                                    : "mobile-meeting-audio-shell"
                            }
                        >
                            {dyteMeetingComponent}
                        </div>
                    </main>

                    <ActionButtons
                        isModerator={isModerator}
                        isMobile={true}
                        roomId={roomId}
                        user={user}
                        isLeavingRoom={isLeavingRoom}
                        isMeetingLoaded={sessionJoined}
                        setShowInviteModal={setShowInviteModal}
                        setIsPanelOpen={setIsPanelOpen}
                        handleLeaveMeeting={handleLeaveMeeting}
                        onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
                        onAddSceneObject={() => setIsSceneObjectModalOpen(true)}
                        pendingApprovalsCount={isModerator ? accessRequests.length : 0}
                        onShowRoomTour={() => setForceRoomTour(true)}
                        isPortrait={isPortrait}
                        activeMobileTab={activeTab}
                    />
                </div>
                ) : (
                /* Desktop */
                <div className="flex-1 min-h-0 flex relative">
                    <main className="flex-1 flex min-h-0 pb-20">
                        <div data-tour="scene" className="flex-1 relative min-w-0">
                            <div className="absolute inset-0">
                                <Scene
                                    roomId={roomId}
                                    onError={handleSceneError}
                                    sessionSetting={
                                        sessionSetting
                                            ? {
                                                ...sessionSetting,
                                                isRoomModerator: isModerator,
                                                isRoomOwner: isOwner,
                                            }
                                            : sessionSetting
                                    }
                                    meetingClient={meetingClient}
                                    layoutMode="desktop"
                                />
                            </div>
                        </div>

                        <aside data-tour="video" className="w-80 bg-gray-800/95 backdrop-blur-md shadow-2xl border-l border-gray-700/50 flex flex-col">
                            <header className="p-3 border-b border-gray-700/50">
                                <h3 className="text-base font-semibold text-gray-100 flex items-center space-x-2">
                                    <Video className="w-4 h-4 text-blue-500" />
                                    <span>Videochamada</span>
                                </h3>
                            </header>
                            <div className="flex-1 min-h-0">
                                <div className="h-full w-full bg-gray-900 overflow-hidden">
                                    {dyteMeetingComponent}
                                </div>
                            </div>
                        </aside>
                    </main>
                    <footer data-tour="actions" className="absolute bottom-0 left-0 right-0 z-[60]">
                        <div className="px-4 py-3 flex justify-center">
                            <ActionButtons
                                isModerator={isModerator}
                                isMobile={false}
                                roomId={roomId}
                                user={user}
                                isLeavingRoom={isLeavingRoom}
                                isMeetingLoaded={sessionJoined}
                                setShowInviteModal={setShowInviteModal}
                                setIsPanelOpen={setIsPanelOpen}
                                handleLeaveMeeting={handleLeaveMeeting}
                                onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
                                onAddSceneObject={() => setIsSceneObjectModalOpen(true)}
                                pendingApprovalsCount={isModerator ? accessRequests.length : 0}
                                onShowRoomTour={() => setForceRoomTour(true)}
                            />
                        </div>
                    </footer>
                </div>
                )}
            </div>
        </div>
    );
}

export default Room;